import { useEffect, useState } from "react";
import { Plus, ShoppingCart, Trash2, Save, Search } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, euros, formatDate } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const STATUS_LABELS = {
  en_attente_paiement: "En attente de paiement", payee: "Payée", a_preparer: "À préparer",
  preparee: "Préparée", expediee: "Expédiée", disponible_retrait: "Disponible au retrait",
  livree: "Livrée", annulee: "Annulée", remboursee: "Remboursée",
};
const PAYMENT_LABELS = { en_attente: "En attente", payee: "Payée", echouee: "Échouée", remboursee: "Remboursée", partiellement_remboursee: "Partiellement remboursée" };
const KIND_LABELS = { pickup: "Retrait partenaire", relay: "Point relais", home: "Domicile" };

const EMPTY_ORDER = {
  customer_name: "", customer_email: "", customer_phone: "",
  items: [], delivery_kind: "relay", delivery_label: "", shipping_cost: 4.9,
  address: "", zip: "", city: "", pickup_name: "", relay_name: "",
  discount: 0, promo_code: "", comment: "", status: "payee", payment_status: "payee", tracking_number: "",
};

const selectCls = "w-full border border-input bg-white px-3 py-2 text-sm";

function Badge({ label, tone }) {
  const tones = {
    green: "bg-field/10 text-field", red: "bg-rouge/10 text-rouge",
    amber: "bg-gold/20 text-onyx", gray: "bg-sand text-muted-foreground",
  };
  return <span className={`text-[11px] font-bold uppercase tracking-wide px-2 py-1 ${tones[tone] || tones.gray}`}>{label}</span>;
}

const paymentTone = (s) => (s === "payee" ? "green" : s === "remboursee" || s === "echouee" ? "red" : "amber");
const statusTone = (s) => (["livree", "preparee", "expediee", "disponible_retrait"].includes(s) ? "green" : ["annulee", "remboursee"].includes(s) ? "red" : "amber");

export default function OrdersAdmin() {
  const [orders, setOrders] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);
  const [creating, setCreating] = useState(null);
  const [saving, setSaving] = useState(false);
  const [refundAmount, setRefundAmount] = useState("");
  const [refunding, setRefunding] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [methods, setMethods] = useState([]);
  const [pickups, setPickups] = useState([]);

  const load = () => {
    const params = new URLSearchParams();
    if (statusFilter) params.set("status", statusFilter);
    if (q) params.set("q", q);
    api.get(`/admin/orders?${params}`).then((r) => setOrders(r.data)).catch((e) => toast.error(apiError(e)));
  };
  useEffect(() => { load(); }, [statusFilter]);
  useEffect(() => {
    api.get("/admin/pricing").then((r) => setPricing(r.data)).catch(() => {});
    api.get("/admin/delivery-methods").then((r) => setMethods(r.data)).catch(() => {});
    api.get("/admin/pickup-locations").then((r) => setPickups(r.data.filter((p) => p.active))).catch(() => {});
  }, []);

  const openCreate = () => {
    const relay = methods.find((m) => m.kind === "relay") || methods[0];
    setCreating({ ...EMPTY_ORDER, shipping_cost: relay?.price ?? 0, delivery_label: relay?.name || "" });
  };

  const setOffer = (n) => {
    const price = n === 1 ? pricing?.price_1 ?? 34.9 : pricing?.price_2 ?? 59;
    setCreating((c) => ({ ...c, items: [{ label: `Douzième Homme — Le Jeu · Édition Nice (×${n})`, qty: n, unit_price: n === 1 ? price : price / 2 }] }));
  };

  const setMethod = (id) => {
    const m = methods.find((x) => x.id === id);
    if (!m) return;
    setCreating((c) => ({ ...c, delivery_kind: m.kind, delivery_label: m.name, shipping_cost: m.price }));
  };

  const create = async () => {
    if (!creating.items.length) { toast.error("Choisissez une formule (1 ou 2 boîtes)"); return; }
    setSaving(true);
    try {
      const payload = { ...creating, discount: Number(creating.discount) || 0, shipping_cost: Number(creating.shipping_cost) || 0 };
      await api.post("/admin/orders", payload);
      toast.success("Commande créée");
      setCreating(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const saveDetail = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/orders/${detail.id}`, {
        status: detail.status, payment_status: detail.payment_status,
        tracking_number: detail.tracking_number, comment: detail.comment,
      });
      toast.success("Commande mise à jour");
      setDetail(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette commande ? Le stock sera réajusté.")) return;
    await api.delete(`/admin/orders/${id}`);
    toast.success("Commande supprimée");
    setDetail(null);
    load();
  };

  const refund = async () => {
    if (!window.confirm("Confirmer le remboursement Stripe ? Cette action est définitive.")) return;
    setRefunding(true);
    try {
      const { data } = await api.post(`/admin/orders/${detail.id}/refund`, { amount: refundAmount ? Number(refundAmount) : null });
      setDetail(data);
      setRefundAmount("");
      toast.success(`Remboursement de ${euros(data.refund_amount)} effectué`);
      load();
    } catch (e) {
      toast.error(apiError(e, "Remboursement impossible"));
    } finally {
      setRefunding(false);
    }
  };

  const field = (k, label, props = {}) => (
    <div>
      <Label>{label}</Label>
      <Input className="mt-1.5" value={creating[k]} onChange={(e) => setCreating({ ...creating, [k]: e.target.value })} {...props} />
    </div>
  );

  return (
    <div data-testid="admin-orders">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight uppercase text-onyx">Commandes</h1>
        <button onClick={openCreate} data-testid="order-create"
          className="inline-flex items-center gap-2 bg-rouge text-white px-5 py-2.5 text-sm font-bold uppercase tracking-wide hover:bg-rouge-hover transition-colors">
          <Plus className="w-4 h-4" /> Commande manuelle
        </button>
      </div>

      <div className="flex gap-3 mb-6 flex-wrap">
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="orders-status-filter" className={`${selectCls} max-w-xs`}>
          <option value="">Tous les statuts</option>
          {Object.entries(STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="N°, client, email…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
            data-testid="orders-search" className="pl-9 bg-white" />
        </div>
      </div>

      {!orders ? <p className="text-muted-foreground">Chargement…</p> : orders.length === 0 ? (
        <div className="bg-white border border-line p-10 text-center" data-testid="orders-empty">
          <ShoppingCart className="w-8 h-8 text-rouge mx-auto mb-3" />
          <p className="font-display font-bold text-onyx">Aucune commande pour le moment</p>
          <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
            Les commandes Stripe apparaîtront ici automatiquement à l'activation du paiement (phase 2).
            Vous pouvez déjà saisir une commande manuelle (vente par téléphone, en salon…).
          </p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="orders-list">
          {orders.map((o) => (
            <button key={o.id} onClick={() => setDetail({ ...o })} data-testid={`order-row-${o.number}`}
              className="w-full text-left bg-white border border-line p-5 hover:border-rouge/40 transition-colors">
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex-1 min-w-[180px]">
                  <p className="font-display font-bold text-onyx">{o.number} — {o.customer_name}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {formatDate(o.created_at)} · {o.items.map((i) => `${i.qty}× jeu`).join(", ")} · {KIND_LABELS[o.delivery_kind] || o.delivery_label}
                  </p>
                </div>
                <Badge label={PAYMENT_LABELS[o.payment_status] || o.payment_status} tone={paymentTone(o.payment_status)} />
                <Badge label={STATUS_LABELS[o.status] || o.status} tone={statusTone(o.status)} />
                <p className="font-display font-bold text-onyx">{euros(o.total)}</p>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Fiche détaillée */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="order-detail">
          <DialogHeader><DialogTitle className="font-display">Commande {detail?.number}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-5 text-sm">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-sand p-4">
                  <p className="font-bold text-onyx mb-2">Client</p>
                  <p>{detail.customer_name}</p>
                  {detail.customer_email && <p className="text-muted-foreground">{detail.customer_email}</p>}
                  {detail.customer_phone && <p className="text-muted-foreground">{detail.customer_phone}</p>}
                </div>
                <div className="bg-sand p-4">
                  <p className="font-bold text-onyx mb-2">Remise — {KIND_LABELS[detail.delivery_kind] || detail.delivery_label}</p>
                  {detail.delivery_kind === "home" && <p>{detail.address} {detail.zip} {detail.city}</p>}
                  {detail.delivery_kind === "pickup" && <p>Point partenaire : {detail.pickup_name || "—"}</p>}
                  {detail.delivery_kind === "relay" && <p>Point relais : {detail.relay_name || "à sélectionner (Sendcloud — phase 2)"}</p>}
                  {detail.tracking_number && <p className="mt-1">Suivi : <span className="font-mono">{detail.tracking_number}</span></p>}
                </div>
              </div>
              <div>
                <p className="font-bold text-onyx mb-2">Produits</p>
                <ul className="divide-y divide-line border border-line">
                  {detail.items.map((i, k) => (
                    <li key={k} className="flex justify-between px-4 py-2.5"><span>{i.label}</span><span className="font-semibold">{euros(i.qty * i.unit_price)}</span></li>
                  ))}
                </ul>
                <dl className="mt-3 space-y-1 text-right">
                  <div className="flex justify-between text-muted-foreground"><dt>Sous-total</dt><dd>{euros(detail.subtotal)}</dd></div>
                  {detail.discount > 0 && <div className="flex justify-between text-rouge"><dt>Remise{detail.promo_code ? ` (${detail.promo_code})` : ""}</dt><dd>-{euros(detail.discount)}</dd></div>}
                  <div className="flex justify-between text-muted-foreground"><dt>Livraison</dt><dd>{detail.shipping_cost === 0 ? "Gratuit" : euros(detail.shipping_cost)}</dd></div>
                  <div className="flex justify-between font-display font-bold text-onyx text-base"><dt>Total</dt><dd data-testid="order-detail-total">{euros(detail.total)}</dd></div>
                  {detail.refund_amount > 0 && <div className="flex justify-between text-rouge"><dt>Remboursé le {(detail.refunded_at || "").slice(0, 10)}</dt><dd className="font-semibold" data-testid="order-refund-amount">-{euros(detail.refund_amount)}</dd></div>}
                </dl>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Statut commande</Label>
                  <select className={`${selectCls} mt-1.5`} value={detail.status} onChange={(e) => setDetail({ ...detail, status: e.target.value })} data-testid="order-status-select">
                    {Object.entries(STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Statut paiement</Label>
                  <select className={`${selectCls} mt-1.5`} value={detail.payment_status} onChange={(e) => setDetail({ ...detail, payment_status: e.target.value })} data-testid="order-payment-select">
                    {Object.entries(PAYMENT_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <Label>Numéro de suivi</Label>
                <Input className="mt-1.5 font-mono" value={detail.tracking_number || ""} onChange={(e) => setDetail({ ...detail, tracking_number: e.target.value })} data-testid="order-tracking-input" placeholder="Sera rempli par Sendcloud (phase 2)" />
              </div>
              <div>
                <Label>Commentaires internes</Label>
                <Textarea rows={2} className="mt-1.5" value={detail.comment || ""} onChange={(e) => setDetail({ ...detail, comment: e.target.value })} data-testid="order-comment-input" />
              </div>
              <div className="bg-sand p-4 space-y-1.5" data-testid="order-integrations">
                <p className="font-bold text-onyx">Intégrations</p>
                <p className="text-xs text-muted-foreground font-mono break-all">Stripe Session : {detail.stripe_session_id || "—"}</p>
                <p className="text-xs text-muted-foreground font-mono break-all">Payment Intent : {detail.stripe_payment_intent_id || "—"}</p>
                <p className="text-xs text-muted-foreground font-mono break-all">Sendcloud Parcel : {detail.sendcloud_parcel_id || "—"}</p>
                {detail.carrier && <p className="text-xs text-muted-foreground">Transporteur : {detail.carrier}</p>}
                {detail.sendcloud_status && <p className="text-xs text-muted-foreground">Statut colis : {detail.sendcloud_status}</p>}
              </div>
              {(detail.payment_status === "payee" || detail.payment_status === "partiellement_remboursee") && detail.stripe_payment_intent_id && (
                <div className="border border-rouge/30 p-4" data-testid="refund-section">
                  <p className="font-bold text-onyx text-sm mb-2">Remboursement (Stripe)</p>
                  <div className="flex gap-2">
                    <Input type="number" step="0.01" min="0" placeholder={`Total ou partiel — reste ${euros(detail.total - (detail.refund_amount || 0))}`}
                      value={refundAmount} onChange={(e) => setRefundAmount(e.target.value)} data-testid="refund-amount-input" className="bg-white" />
                    <button onClick={refund} disabled={refunding} data-testid="refund-button"
                      className="shrink-0 bg-onyx text-cream px-5 text-sm font-bold uppercase tracking-wide hover:bg-rouge transition-colors disabled:opacity-50">
                      {refunding ? "…" : "Rembourser"}
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">Vide = remboursement total. Le stock est réajusté si la commande n'est pas expédiée.</p>
                </div>
              )}
              <div className="flex gap-3">
                <button onClick={saveDetail} disabled={saving} data-testid="order-save"
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-rouge text-white px-6 py-3 text-sm font-bold uppercase tracking-wide hover:bg-rouge-hover disabled:opacity-50">
                  <Save className="w-4 h-4" /> {saving ? "Enregistrement…" : "Enregistrer"}
                </button>
                <button onClick={() => remove(detail.id)} data-testid="order-delete"
                  className="inline-flex items-center gap-2 border-2 border-destructive text-destructive px-5 py-3 text-sm font-bold uppercase hover:bg-destructive hover:text-white transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Création manuelle */}
      <Dialog open={!!creating} onOpenChange={(o) => !o && setCreating(null)}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="order-create-dialog">
          <DialogHeader><DialogTitle className="font-display">Nouvelle commande manuelle</DialogTitle></DialogHeader>
          {creating && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <button onClick={() => setOffer(1)} data-testid="create-offer-1"
                  className={`p-4 border-2 text-left ${creating.items[0]?.qty === 1 ? "border-rouge" : "border-line"}`}>
                  <p className="font-display font-bold text-onyx">1 boîte</p>
                  <p className="text-sm text-muted-foreground">{euros(pricing?.price_1 ?? 34.9)}</p>
                </button>
                <button onClick={() => setOffer(2)} data-testid="create-offer-2"
                  className={`p-4 border-2 text-left ${creating.items[0]?.qty === 2 ? "border-rouge" : "border-line"}`}>
                  <p className="font-display font-bold text-onyx">2 boîtes</p>
                  <p className="text-sm text-muted-foreground">{euros(pricing?.price_2 ?? 59)}</p>
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {field("customer_name", "Nom du client *", { "data-testid": "create-name" })}
                {field("customer_email", "Email", { type: "email", "data-testid": "create-email" })}
                {field("customer_phone", "Téléphone", { "data-testid": "create-phone" })}
                <div>
                  <Label>Mode de remise</Label>
                  <select className={`${selectCls} mt-1.5`} data-testid="create-delivery"
                    value={methods.find((m) => m.kind === creating.delivery_kind)?.id || ""} onChange={(e) => setMethod(e.target.value)}>
                    {methods.map((m) => <option key={m.id} value={m.id}>{m.name} — {m.price === 0 ? "Gratuit" : euros(m.price)}</option>)}
                  </select>
                </div>
              </div>
              {creating.delivery_kind === "pickup" && (
                <div>
                  <Label>Point de retrait partenaire</Label>
                  <select className={`${selectCls} mt-1.5`} data-testid="create-pickup" value={creating.pickup_name} onChange={(e) => setCreating({ ...creating, pickup_name: e.target.value })}>
                    <option value="">Choisir…</option>
                    {pickups.map((p) => <option key={p.id} value={p.name}>{p.name} — {p.address}</option>)}
                  </select>
                </div>
              )}
              {creating.delivery_kind === "relay" && (
                <div>
                  <Label>Point relais</Label>
                  <Input className="mt-1.5" disabled value="Sélection Sendcloud à l'intégration (phase 2)" data-testid="create-relay" />
                </div>
              )}
              {creating.delivery_kind === "home" && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="sm:col-span-3">{field("address", "Adresse", { "data-testid": "create-address" })}</div>
                  {field("zip", "Code postal", { "data-testid": "create-zip" })}
                  {field("city", "Ville", { "data-testid": "create-city" })}
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {field("shipping_cost", "Frais de livraison (€)", { type: "number", step: "0.01", "data-testid": "create-shipping" })}
                {field("discount", "Remise (€)", { type: "number", step: "0.01", "data-testid": "create-discount" })}
                {field("promo_code", "Code promo", { "data-testid": "create-promo" })}
              </div>
              {field("comment", "Commentaire interne", { "data-testid": "create-comment" })}
              <div className="flex justify-between items-center bg-onyx text-cream px-4 py-3">
                <span className="text-sm font-bold uppercase tracking-wide">Total</span>
                <span className="font-display text-xl font-extrabold" data-testid="create-total">
                  {euros((creating.items.reduce((s, i) => s + i.qty * i.unit_price, 0)) - (Number(creating.discount) || 0) + (Number(creating.shipping_cost) || 0))}
                </span>
              </div>
              <button onClick={create} disabled={saving || !creating.customer_name} data-testid="create-submit"
                className="w-full bg-rouge text-white px-6 py-3 text-sm font-bold uppercase tracking-wide hover:bg-rouge-hover disabled:opacity-50">
                {saving ? "Création…" : "Créer la commande"}
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
